/**
 * CDN Management — per-domain CDN platform management for the Multi-CDN tab. Pick
 * a domain, then for each attached CDN platform (VNCDN, Akamai, Aliyun, …) toggle
 * it on/off and switch between Default (Mlytics-managed) and BYO modes.
 *
 * Backed by `/waf/platforms/domain*` (Owl/Mlytics DomainsService). The backend
 * exposes only enable/disable (default mode) + BYO edge-domain — no attach/detach
 * of arbitrary platforms and no weight/priority config.
 */
import { useEffect, useMemo, useState } from 'react';
import { Boxes, RotateCcw } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import {
  Badge,
  Skeleton,
  Button,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafDomainPlatforms,
  useSetPlatformDefault,
  useWafDomainDetail,
  useUpdateWafDomainSettings,
  wafExtractError,
  type DomainPlatform,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import { cap } from '../domains/format';
import { PlatformByoDialog } from './PlatformByoDialog';
import styles from './WafCdnManagement.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;
const mono = { fontFamily: 'var(--vn-font-mono)' } as const;

/** Platform status → badge tone. */
function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  const s = status.toLowerCase();
  if (['completed', 'active', 'usable', 'enabled', 'done', 'success'].includes(s)) return 'success';
  if (['in_progress', 'pending', 'processing', 'updating'].includes(s)) return 'warning';
  if (['failed', 'error'].includes(s)) return 'danger';
  return 'neutral';
}

/** Enable/disable a platform via the default-mode endpoint. */
function PlatformEnableCell({ serviceId, domain, p }: { serviceId: string; domain: string; p: DomainPlatform }) {
  const { show } = useToast();
  const setDefault = useSetPlatformDefault(serviceId, {
    onSuccess: (_d, vars) => {
      show({ tone: 'success', title: vars.enable ? 'Platform enabled' : 'Platform disabled' });
      trackFeatureAction('waf', 'cdn', vars.enable ? 'enable' : 'disable');
    },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Switch
        checked={p.enable}
        disabled={setDefault.isPending}
        aria-label={`Enable ${p.display_name || p.name}`}
        onCheckedChange={(enable) => setDefault.mutate({ domain, platform: p.name, enable })}
      />
    </div>
  );
}

/** Per-row actions: set/edit BYO edge domain; switch a BYO platform back to default. */
function PlatformActionsCell({ serviceId, domain, p }: { serviceId: string; domain: string; p: DomainPlatform }) {
  const { show } = useToast();
  const [byoOpen, setByoOpen] = useState(false);
  const isByo = p.type === 'byo';

  const setDefault = useSetPlatformDefault(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Switched to default mode' }); trackFeatureAction('waf', 'cdn', 'edit'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  // Managed platforms have no per-row action (BYO can't be set from here);
  // BYO platforms keep Edit CNAME + Switch to default.
  if (!isByo) return muted('—');

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      <Button size="sm" variant="secondary" onClick={() => setByoOpen(true)}>
        Edit CNAME
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={setDefault.isPending}
        onClick={() => setDefault.mutate({ domain, platform: p.name, enable: true })}
      >
        Switch to default
      </Button>
      {byoOpen && (
        <PlatformByoDialog serviceId={serviceId} domain={domain} platform={p} open={byoOpen} onOpenChange={setByoOpen} />
      )}
    </div>
  );
}

/** A domain's CDN platforms — table + pagination. */
function PlatformsCard({ serviceId, domain }: { serviceId: string; domain: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => setPage(1), [domain]);

  const { data, isLoading, isError, isFetching, refetch } = useWafDomainPlatforms(serviceId, domain, {
    page,
    page_size: pageSize,
  });

  const rows = data?.items ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = Math.max(1, data?.pagination?.total_pages ?? 1);

  const columns = useMemo<ColumnDef<DomainPlatform, unknown>[]>(
    () => [
      {
        id: 'platform',
        header: 'Platform',
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontWeight: 500 }}>{p.display_name || p.name}</span>
              {p.display_name && p.name !== p.display_name ? (
                <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)', ...mono }}>{p.name}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.type === 'byo' ? (
            <Badge tone="warning" appearance="subtle" size="sm">Custom CDN</Badge>
          ) : (
            <Badge tone="neutral" appearance="subtle" size="sm">Managed CDN</Badge>
          ),
      },
      {
        id: 'edge',
        header: 'CNAME',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.edge_domain ? <span style={mono}>{row.original.edge_domain}</span> : muted('—'),
      },
      {
        id: 'websocket',
        header: 'Support Websocket',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.websocket ? (
            <Badge tone="success" appearance="subtle" size="sm">Yes</Badge>
          ) : (
            <Badge tone="neutral" appearance="subtle" size="sm">No</Badge>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.status ? (
            <Badge tone={statusTone(row.original.status)} appearance="subtle" size="sm">{cap(row.original.status)}</Badge>
          ) : (
            muted('—')
          ),
      },
      {
        id: 'enable',
        header: 'Enabled',
        enableSorting: false,
        cell: ({ row }) => <PlatformEnableCell serviceId={serviceId} domain={domain} p={row.original} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => <PlatformActionsCell serviceId={serviceId} domain={domain} p={row.original} />,
      },
    ],
    [serviceId, domain],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>CDN Platforms{total ? ` (${total})` : ''}</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.toolbar}>
          <div className={styles.filters}>
            <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
              Enable/disable applies Mlytics-managed (default) mode; use BYO to route through your own edge domain.
            </span>
          </div>
          <div className={styles.actions}>
            <Button variant="secondary" startIcon={<RotateCcw size={15} />} onClick={() => refetch()} aria-label="Refresh CDN platforms">
              Refresh
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
          </div>
        ) : isError ? (
          <Alert tone="danger" title="Couldn't load CDN platforms" actions={<button onClick={() => refetch()}>Retry</button>}>
            The WAF service couldn't return this domain's CDN platforms. Try again.
          </Alert>
        ) : total === 0 ? (
          <EmptyState
            icon={<Boxes size={20} />}
            title="No CDN platforms"
            description="This domain has no CDN platforms to manage yet."
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(r) => r.platform_id || r.name}
              emptyState="No CDN platforms."
              footer={`Showing ${rows.length ? (page - 1) * pageSize + 1 : 0}–${(page - 1) * pageSize + rows.length} of ${total}`}
            />
            {(pageCount > 1 || PAGE_SIZE_OPTIONS.length > 1) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--vn-space-4)',
                  marginTop: 'var(--vn-space-4)',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                  <label htmlFor="waf-cdn-page-size" style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                    Rows per page
                  </label>
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                    <SelectTrigger id="waf-cdn-page-size" size="sm" style={{ width: 72 }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {pageCount > 1 && (
                  <Pagination
                    page={page}
                    pageCount={pageCount}
                    onPageChange={setPage}
                    summary={isFetching ? 'Loading…' : `Page ${page} of ${pageCount}`}
                  />
                )}
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

/** A label + description row whose control is an on/off Switch. */
function ToggleRow({
  label,
  description,
  checked,
  pending,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  pending: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span style={{ fontWeight: 500 }}>{label}</span>
        <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>{description}</span>
      </div>
      <Switch checked={checked} aria-label={label} disabled={pending} onCheckedChange={onChange} />
    </div>
  );
}

/** Domain-level CDN delivery toggles, patched via `/waf/domains/settings`. */
function DomainSettingsCard({ serviceId, domain }: { serviceId: string; domain: string }) {
  const { show } = useToast();
  const detailQ = useWafDomainDetail(serviceId, domain);
  const d = detailQ.data;
  const mut = useUpdateWafDomainSettings(serviceId, domain, {
    onSuccess: () => { show({ tone: 'success', title: 'Settings updated' }); trackFeatureAction('waf', 'cdn_settings', 'edit'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Domain settings</CardTitle>
      </CardHeader>
      <CardBody>
        {detailQ.isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
          </div>
        ) : detailQ.isError ? (
          <Alert tone="danger" title="Couldn't load settings" actions={<button onClick={() => detailQ.refetch()}>Retry</button>}>
            The WAF service couldn't return this domain's settings. Try again.
          </Alert>
        ) : d ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <ToggleRow
              label="Auto compression"
              description="Compress eligible responses (Gzip/Brotli) at the edge before delivery."
              checked={d.auto_compression}
              pending={mut.isPending}
              onChange={(v) => mut.mutate({ auto_compression: v })}
            />
            <ToggleRow
              label="Ignore query string"
              description="Cache regardless of the request's query string (treat URLs as identical)."
              checked={d.ignore_query_string}
              pending={mut.isPending}
              onChange={(v) => mut.mutate({ ignore_query_string: v })}
            />
            <ToggleRow
              label="WebSocket"
              description="Allow WebSocket connections to pass through to the origin."
              checked={d.websocket_enable}
              pending={mut.isPending}
              onChange={(v) => mut.mutate({ websocket_enable: v })}
            />
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

export function WafCdnManagement({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  // A zone is selected but has no proxied subdomain → nothing to manage.
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Boxes size={22} />}
        title="CDN Management"
        description="Manage the CDN platforms serving the selected subdomain — enable/disable and switch between default and BYO modes."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Boxes size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to manage CDN platforms for. Proxy a record first."
        />
      ) : !domain ? (
        <EmptyState
          icon={<Boxes size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to manage its CDN platforms."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <PlatformsCard serviceId={serviceId} domain={domain} />
          <DomainSettingsCard serviceId={serviceId} domain={domain} />
        </div>
      )}
    </Container>
  );
}

export default WafCdnManagement;
